import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Building2, ShieldCheck, User, Mail, Phone, ArrowRight, PartyPopper, Loader2, X } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { api, formatApiErrorDetail } from "@/lib/api";

const roles = [
  { id: "agent", label: "Real Estate Agent", icon: Building2 },
  { id: "security", label: "Security Company", icon: ShieldCheck },
];

const InputRow = ({ icon: Icon, ...props }) => (
  <div className="flex items-center gap-3 bg-transparent border border-[#1E293B] rounded-xl px-4 focus-within:border-[#D4AF37] transition-colors duration-200">
    <Icon className="w-5 h-5 text-[#94A3B8] shrink-0" />
    <input
      {...props}
      className="flex-1 bg-transparent border-0 outline-none text-[#F8FAFC] font-body text-base py-3.5 placeholder:text-[#94A3B8] focus:ring-0"
    />
  </div>
);

export const Waitlist = () => {
  const [role, setRole] = useState("agent");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const closeConfirmation = () => {
    setDone(false);
    setForm({ name: "", email: "", phone: "" });
    setRole("agent");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", { role, ...form });
      setDone(true);
      toast.success(data.duplicate ? "You're already on the list!" : "You're on the list!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="relative py-28 lg:py-36 scroll-mt-20">
      <div className="absolute inset-0 gold-spot pointer-events-none" />
      <div className="relative max-w-2xl mx-auto px-6 lg:px-10">
        <Reveal className="text-center">
          <span className="kicker">Get Early Access</span>
          <h2 className="font-head font-black tracking-tight text-[#F8FAFC] text-4xl lg:text-5xl mt-5 leading-[1.05]">
            Join the founding waitlist.
          </h2>
          <p className="font-body text-[#CBD5E1] text-base mt-5 max-w-lg mx-auto leading-relaxed">
            Early members get priority onboarding and launch-day perks.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 bg-[#0F172A]/70 backdrop-blur-xl border border-[#1E293B] rounded-[20px] p-6 sm:p-9 shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-5"
            >
                  <div>
                    <label className="block font-body text-sm font-medium text-[#CBD5E1] mb-2">I am a…</label>
                    <div className="grid grid-cols-2 gap-2 bg-[#050914] p-1 rounded-full border border-[#1E293B]">
                      {roles.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setRole(r.id)}
                          data-testid={`role-${r.id}`}
                          className="relative flex items-center justify-center gap-2 py-2.5 rounded-full font-head font-bold text-sm transition-colors duration-200"
                        >
                          {role === r.id && (
                            <motion.span
                              layoutId="activeRole"
                              className="absolute inset-0 bg-[#D4AF37] rounded-full"
                              transition={{ type: "spring", stiffness: 400, damping: 32 }}
                            />
                          )}
                          <span className={`relative z-10 flex items-center gap-2 ${role === r.id ? "text-[#050914]" : "text-[#94A3B8]"}`}>
                            <r.icon className="w-4 h-4" />
                            {r.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block font-body text-sm font-medium text-[#CBD5E1] mb-2">Full Name</label>
                    <InputRow icon={User} data-testid="waitlist-name" value={form.name} onChange={set("name")} placeholder="Jordan Carter" />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-[#CBD5E1] mb-2">Email</label>
                    <InputRow icon={Mail} type="email" data-testid="waitlist-email" value={form.email} onChange={set("email")} placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-[#CBD5E1] mb-2">Phone Number</label>
                    <InputRow icon={Phone} data-testid="waitlist-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="waitlist-submit"
                    className="group w-full font-head font-bold text-base bg-[#D4AF37] text-[#050914] h-[54px] rounded-xl hover:bg-[#E4C569] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Join the Waitlist <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" /></>}
                  </button>

                  <p className="font-body text-xs text-[#94A3B8] text-center leading-relaxed">
                    We'll only use your info to notify you at launch. No spam, ever. See our Privacy Policy.
                  </p>
            </motion.form>
          </div>
        </Reveal>
      </div>

      {/* Full-screen confirmation takeover */}
      <AnimatePresence>
        {done && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="waitlist-confirmation-overlay"
            className="fixed inset-0 z-[100] flex items-center justify-center px-6 bg-[#050914]/85 backdrop-blur-xl"
            onClick={closeConfirmation}
          >
            <div className="absolute inset-0 gold-spot pointer-events-none" />
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="waitlist-confirmation"
              className="relative w-full max-w-lg bg-[#0F172A] border border-[#1E293B] rounded-[20px] p-8 sm:p-12 text-center shadow-[0_16px_60px_rgba(0,0,0,0.6)]"
            >
              <button
                onClick={closeConfirmation}
                data-testid="confirmation-close-btn"
                aria-label="Close"
                className="absolute top-5 right-5 w-10 h-10 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B] transition-colors duration-200"
              >
                <X className="w-5 h-5" />
              </button>

              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 320, damping: 16 }}
                className="w-20 h-20 rounded-full bg-[#292414] flex items-center justify-center mx-auto"
              >
                <PartyPopper className="w-10 h-10 text-[#D4AF37]" />
              </motion.div>

              <h3 className="font-head font-black text-3xl text-[#F8FAFC] mt-8">You're on the list!</h3>
              <p className="font-body text-[#CBD5E1] text-base mt-4 leading-relaxed max-w-md mx-auto">
                We'll be in touch as we roll out in your area. Keep an eye on your inbox.
              </p>

              <button
                onClick={closeConfirmation}
                data-testid="confirmation-back-btn"
                className="mt-10 font-head font-bold text-base bg-[#D4AF37] text-[#050914] px-8 h-[52px] rounded-xl hover:bg-[#E4C569] transition-colors duration-200"
              >
                Back to Site
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
